import { useEffect, useRef } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import campaignApi from '../../services/campaignApi'
import { useServerTableState, UseServerTableStateOptions } from '../useServerTableState'

export interface UseCampaignMessageStatsOptions extends UseServerTableStateOptions {
  isRefreshing?: boolean
}

export function useCampaignMessageStats(
  campaignId: string,
  options: UseCampaignMessageStatsOptions = {}
) {
  const { initialPageSize = 10, isRefreshing = false, ...tableOptions } = options

  const tableState = useServerTableState({
    initialPageSize,
    ...tableOptions,
  })

  const {
    paginationModel,
    sortBy,
    sortOrder,
    searchQuery,
    dynamicFilters,
    clearAllFilters,
  } = tableState

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'campaignMessages',
      campaignId,
      paginationModel.page,
      paginationModel.pageSize,
      sortBy,
      sortOrder,
      searchQuery,
      dynamicFilters,
    ],
    queryFn: () =>
      campaignApi.getCampaignMessagesStats(campaignId, {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        dynamicFilters,
      }),
    enabled: !!campaignId,
    // Keep data cached across tab switches (component unmount/remount) and window focus.
    // Fresh data is triggered explicitly by the user via the refresh button.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  })

  // Re-fetch when parent triggers refresh
  const prevRefreshingRef = useRef(isRefreshing)
  useEffect(() => {
    if (isRefreshing && !prevRefreshingRef.current) {
      refetch()
    }
    prevRefreshingRef.current = isRefreshing
  }, [isRefreshing, refetch])

  return {
    ...tableState,
    handleClearFilters: clearAllFilters,
    messages: data?.messages || [],
    isLoading,
    totalMessages: data?.total || 0,
    refetch,
    campaignId,
  }
}

export default useCampaignMessageStats
